import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from '@usas/ui-web/wrappers/ReactRouterDom';

import NavLinks from './NavLinks';
import MeetNavLinks from '../meet/NavLinks';

import useOmeMeetData from '../../state/omeMeet/UseOmeMeetData';
import useOmeMeetEntryData from '../../state/omeMeetEntry/UseOmeMeetEntryData';
import useOmeMeetOrgUnitEntryData from '../../state/omeMeetOrgUnitEntry/UseOmeMeetOrgUnitEntryData';
import useOmeMeetOrgUnitEntryInfoData from '../../state/omeMeetOrgUnitEntryInfo/UseOmeMeetOrgUnitEntryInfoData';
import useOmeMeetTeamEntryData from '../../state/omeMeetTeamEntry/UseOmeMeetTeamEntryData';
import useMeetCompetitionSummaryData from '../../state/meetCompetitionSummary/UseMeetCompetitionSummaryData';

import useMeetData from '@usas/ui-web/state/meet/UseMeetData';
import usePersonOrgRoleData from '@usas/ui-web/state/personOrgRole/UsePersonOrgRoleData';
import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';

import UseForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import { formatDate, formatSubmittedDateTimeTwelveHourClock } from '@usas/ui-web/utils/DateFunctions';
import ToIntIfInt from '@usas/ui-web/utils/ToIntIfInt';

const INITIAL_FORM_STATE = {
  omeMeetEntryId: Constants.DEFAULT_ID,
  omeMeetEntryName: '--',
  teamEntryStatusId: Constants.DEFAULT_ID
};

const INITIAL_MODAL_STATE = {
  displayPopUp: false,
  modalTitle: 'Delete Team Entry?',
  meetHost: '',
  meetName: '',
  meetDates: '',
  omeMeetName: '',
  omeMeetEntryDates: '',
  timeZone: '',
  teamName: '',
  teamEntryStatus: '',
  omeMeetId: Constants.DEFAULT_ID,
  omeMeetEntryId: Constants.DEFAULT_ID,
  omeMeetOrgUnitEntryId: Constants.DEFAULT_ID
};

const useTeamEntryManagementById = () => {
  const { environmentVariableState, MY_MEETS_ROLE_GROUP_ID } = useEnvironmentVariableData();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ tryGet: false, tryShowTeamEntry: false, teamEntry: {}, availableOrgUnits: [], tryRedirect: false });
  const [omeMeetTeamEntryStatusState] = useState([
    { id: Constants.DEFAULT_ID, name: "--" },
    { id: 1, name: Constants.WORKFLOW_STATUS_NOT_ENTERED },
    { id: 2, name: Constants.WORKFLOW_STATUS_IN_PROGRESS },
    { id: 3, name: Constants.WORKFLOW_STATUS_SUBMITTED },
    { id: 4, name: Constants.WORKFLOW_STATUS_UNATTACHED }
  ]);
  const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);
  const { meetState, getMeet } = useMeetData();
  const { omeMeetState, getOmeMeetByMeetId, clearOmeMeetObjData, clearOmeMeetArrayData, getOmeMeetOrgUnitEntryPermission } = useOmeMeetData();
  const { clearOmeMeetEntryObjData } = useOmeMeetEntryData();
  const { omeMeetOrgUnitEntryState, postOmeMeetOrgUnitEntry, deleteOmeMeetOrgUnitEntry,
    clearOmeMeetOrgUnitEntryObjData, clearOmeMeetOrgUnitEntryArrayData } = useOmeMeetOrgUnitEntryData();
  const { omeMeetOrgUnitEntryInfoState, clearOmeMeetOrgUnitEntryInfoObjData } = useOmeMeetOrgUnitEntryInfoData();
  const { omeMeetTeamEntryState, getOmeMeetTeamEntry, resetOmeMeetTeamEntryState } = useOmeMeetTeamEntryData();
  const { personOrgRoleState, getPersonOrgRoleByRoleGroup, clearArrayData } = usePersonOrgRoleData();
  const { meetCompetitionSummaryState, getMeetCompetitionSummary } = useMeetCompetitionSummaryData();
  const { targetMeetId } = useParams();
  const meetId = ToIntIfInt(targetMeetId);
  const { formState, setFormState, errorState, setErrors, onFormValueChanged, onValueTextPairChanged, handleSubmit } = UseForm(INITIAL_FORM_STATE, submitFormCallback, () => { });

  const onFilterClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    handleSubmit();
  };

  const onClearFilterClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    getOmeMeetTeamEntry(omeMeetState.objData.omeMeetId, {});
    setFormState(INITIAL_FORM_STATE);
    setErrors({});
  };

  const onEnterMeetClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const url = NavLinks.TEAM_ENTRY_MANAGEMENT_BY_ID + '/' + meetId + NavLinks.TEAM_ENTRY_MANAGEMENT_BY_ID_SELECTION;
    navigate(url, { state: { availableOrgUnits: state.availableOrgUnits } });
  };

  const onStartEntryForATeamClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const url = NavLinks.TEAM_ENTRY_MANAGEMENT_BY_ID + '/' + meetId + NavLinks.TEAM_ENTRY_MANAGEMENT_BY_ID_HQ_SELECTION;
    navigate(url);
  };

  const onAddEntryClicked = (e, teamEntryMeetInfo, teamEntry) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    postOmeMeetOrgUnitEntry(teamEntryMeetInfo?.omeMeetId, teamEntry?.omeMeetEntryId, createOmeMeetOrgUnitEntryObject(teamEntryMeetInfo, teamEntry));
    setState({ ...state, tryShowTeamEntry: true, teamEntry: { ...teamEntry, meetId: meetId, omeMeetId: teamEntryMeetInfo?.omeMeetId } });
  };

  const onViewTeamEntry = (e, teamEntry) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setState({ ...state, tryRedirect: true, teamEntry: teamEntry });
  };

  const onEditTeamEntry = (e, teamEntry) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setState({ ...state, tryRedirect: true, teamEntry: teamEntry });
  };

  const onDeleteTeamEntryClicked = (e, teamEntryMeetInfo, teamEntry) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setModalState({
      ...modalState,
      displayPopUp: true,
      meetHost: meetState.objData.hostOrgUnit?.orgUnitName || '',
      meetName: meetState.objData.meetName || '',
      meetDates: `${meetState.objData?.startDate ? formatDate(meetState.objData?.startDate) : ''} - ${meetState.objData?.endDate ? formatDate(meetState.objData?.endDate) : ''}`,
      omeMeetName: teamEntry?.omeMeetName || '',
      omeMeetEntryDates: `${teamEntry?.meetEntryStartDate ? formatDate(teamEntry?.meetEntryStartDate) : '?'} - ${teamEntry?.meetEntryEndDate ? formatDate(teamEntry?.meetEntryEndDate) : '?'}`,
      timeZone: teamEntry?.timeZone || '',
      teamName: `${teamEntryMeetInfo.orgUnitName || ''} ${teamEntryMeetInfo?.orgUnitName === 'Unattached' ? ' - ' + teamEntryMeetInfo?.parentOrgUnitName : ''} ${teamEntryMeetInfo.parentOrgUnitCode ? '(' + teamEntryMeetInfo.parentOrgUnitCode + ')' : ''}`,
      teamEntryStatus: `${teamEntry.teamEntryStatus || ''} ${teamEntry.orgUnitSubmissionDatetime ? '- ' + formatSubmittedDateTimeTwelveHourClock(teamEntry.orgUnitSubmissionDatetime) + ' ' + teamEntry.timeZone : ''}`,
      omeMeetId: teamEntryMeetInfo?.omeMeetId || Constants.DEFAULT_ID,
      omeMeetEntryId: teamEntry?.omeMeetEntryId || Constants.DEFAULT_ID,
      omeMeetOrgUnitEntryId: teamEntry.omeMeetOrgUnitEntryId || Constants.DEFAULT_ID
    });
  };

  const onModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setModalState(INITIAL_MODAL_STATE);
  };

  const onDeleteTeamEntry = (e, omeMeetId, omeMeetEntryId, omeMeetOrgUnitEntryId) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    deleteOmeMeetOrgUnitEntry(omeMeetId, omeMeetEntryId, omeMeetOrgUnitEntryId);
    onModalCanceled();
    setState({ ...state, tryGet: true });
  }

  //load time
  useEffect(() => {
    if (meetId > 0) {
      if (meetState.isObjLoading !== true && meetState.isObjLoaded !== true) {
        getMeet(meetId);
      }
      if (omeMeetState.isObjLoading !== true && omeMeetState.isObjLoaded !== true) {
        getOmeMeetByMeetId(meetId);
      }
      if (meetCompetitionSummaryState.isObjLoading !== true && meetCompetitionSummaryState.isObjLoaded !== true) {
        getMeetCompetitionSummary(meetId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetId, meetState, omeMeetState, meetCompetitionSummaryState]);

  useEffect(() => {
    if (location.state && location.state.tryGetOmeMeet === true) {
      clearOmeMeetOrgUnitEntryInfoObjData();
      clearOmeMeetOrgUnitEntryObjData();
      clearOmeMeetOrgUnitEntryArrayData();
      clearOmeMeetArrayData();
      resetOmeMeetTeamEntryState();
      clearArrayData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (environmentVariableState.isLoaded === true) {
      getPersonOrgRoleByRoleGroup(0, MY_MEETS_ROLE_GROUP_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentVariableState])

  useEffect(() => {
    if (meetState.isObjLoaded === true && omeMeetState.isObjLoaded === true &&
      omeMeetState.objData?.omeMeetId) {
      getOmeMeetTeamEntry(omeMeetState.objData.omeMeetId, {
        omeMeetEntryId: formState.omeMeetEntryId > 0 ? formState.omeMeetEntryId : undefined,
        teamEntryStatus: formState.teamEntryStatusId > 0 ? (omeMeetTeamEntryStatusState.find(x => x.id === formState.teamEntryStatusId)?.name || undefined) : undefined
      });
      if (omeMeetState.isArrayLoaded === false && omeMeetState.isArrayLoading === false &&
        personOrgRoleState.isArrayLoaded === true) {
        getOmeMeetOrgUnitEntryPermission(omeMeetState.objData.omeMeetId, getUserOrgUnits());
      }
    }
    else if ((meetState.isObjLoaded === true && omeMeetState.isObjLoaded === true &&
      omeMeetState.objData.meetId !== meetState.objData.meetId) || (meetState.isObjLoaded === true &&
        !meetState.omeMeetId)) {
      clearOmeMeetObjData();
      clearOmeMeetEntryObjData();
      clearOmeMeetArrayData();
      resetOmeMeetTeamEntryState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetState.isObjLoaded, omeMeetState.isObjLoaded, personOrgRoleState.isArrayLoaded]);

  useEffect(() => {
    if (state.tryGet === true && omeMeetState.isObjLoaded === true &&
      omeMeetState.objData?.omeMeetId && omeMeetOrgUnitEntryState.isSaving === false
      && personOrgRoleState.isArrayLoaded === true) {
      getOmeMeetTeamEntry(omeMeetState.objData.omeMeetId, {
        omeMeetEntryId: formState.omeMeetEntryId > 0 ? formState.omeMeetEntryId : undefined,
        teamEntryStatus: formState.teamEntryStatusId > 0 ? (omeMeetTeamEntryStatusState.find(x => x.id === formState.teamEntryStatusId)?.name || undefined) : undefined
      });
      getOmeMeetOrgUnitEntryPermission(omeMeetState.objData.omeMeetId, getUserOrgUnits());
      setState({ ...state, tryGet: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tryGet, omeMeetState.isObjLoaded, omeMeetOrgUnitEntryState.isSaving, personOrgRoleState.isArrayLoaded]);

  useEffect(() => {
    if (omeMeetState.isArrayLoaded === true && personOrgRoleState.isArrayLoaded === true && omeMeetTeamEntryState.isObjLoaded === true) {
      const availableOrgUnits = unusedOrgUnitsAvailableToUser();
      setState({ ...state, availableOrgUnits: availableOrgUnits })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetState.isArrayLoaded, personOrgRoleState.isArrayLoaded, omeMeetTeamEntryState.isObjLoaded])

  useEffect(() => {
    if (state.tryShowTeamEntry === true && Object.keys(state.teamEntry).length > 0 && omeMeetOrgUnitEntryState.isSaving === false) {
      navigate(MeetNavLinks.MEET_TEAM_ENTRY_BY_ID + '/' + meetId, { state: { teamEntry: { ...state.teamEntry, omeMeetOrgUnitEntryId: omeMeetOrgUnitEntryState.objData.omeMeetOrgUnitEntryId } } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetOrgUnitEntryState, state.tryShowTeamEntry, state.teamEntry]);

  useEffect(() => {
    if (state.tryRedirect === true && Object.keys(state.teamEntry).length > 0 && Object.keys(state.teamEntry).length > 0) {
      navigate(MeetNavLinks.MEET_TEAM_ENTRY_BY_ID + '/' + meetId, { state: { teamEntry: state.teamEntry } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tryRedirect, state.teamEntry, omeMeetOrgUnitEntryState, omeMeetOrgUnitEntryInfoState,])

  function unusedOrgUnitsAvailableToUser() {
    const unusedOrgUnitsAvailableToUser = [];

    for (let i = 0; i < omeMeetState.arrayData.length; i++) {
      if (omeMeetState.arrayData[i]?.orgUnit?.length > 0) {
        let personOrgRole = undefined;
        for (let j = 0; j < omeMeetState.arrayData[i]?.orgUnit?.length; j++) {
          personOrgRole = personOrgRoleState.arrayData.find(x => x.orgUnitId === omeMeetState.arrayData[i]?.orgUnit[j]?.orgUnitId);
          if (personOrgRole) {
            unusedOrgUnitsAvailableToUser.push({ label: personOrgRole.orgUnitName, value: personOrgRole.orgUnitId });
          }
        }
      }
    }

    return unusedOrgUnitsAvailableToUser;
  }

  function getUserOrgUnits() {
    const userOrgUnitOptions = personOrgRoleState.arrayData.map((personOrgRole) => {
      return {
        orgUnitId: personOrgRole.orgUnitId || '',
      }
    })
    //remove any duplicate options
    const uniqueUserOrgUnitOptions = userOrgUnitOptions.filter((value, index, self) =>
      index === self.findIndex((t) => (
        t.orgUnitId === value.orgUnitId
      ))
    )

    return uniqueUserOrgUnitOptions;
  }

  function submitFormCallback(formState) {
    filter(formState);
  }

  function filter(formState) {
    getOmeMeetTeamEntry(omeMeetState.objData.omeMeetId, {
      omeMeetEntryId: formState.omeMeetEntryId > 0 ? formState.omeMeetEntryId : undefined,
      teamEntryStatus: formState.teamEntryStatusId > 0 ? (omeMeetTeamEntryStatusState.find(x => x.id === formState.teamEntryStatusId)?.name || undefined) : undefined
    });
  }

  function createOmeMeetOrgUnitEntryObject(teamEntryMeetInfo, teamEntry) {
    const todaysDateTime = new Date();
    return {
      omeMeetId: teamEntryMeetInfo?.omeMeetId,
      omeMeetEntryId: teamEntry?.omeMeetEntryId,
      orgUnitId: teamEntryMeetInfo?.orgUnitId,
      orgUnitEntryDatetime: todaysDateTime,
      orgUnitSubmissionDatetime: null,
      orgUnitRegistrationStartDate: null,
      orgUnitRegistrationEndDate: null,
      status: teamEntryMeetInfo?.orgUnitName === 'Unattached' ? Constants.WORKFLOW_STATUS_UNATTACHED : Constants.WORKFLOW_STATUS_IN_PROGRESS,
      orgUnitAthleteRegistrationAllowed: teamEntryMeetInfo?.orgUnitName === 'Unattached' ? true : null
    }
  }

  return {
    state,
    omeMeetTeamEntryStatusState,
    omeMeetState,
    omeMeetOrgUnitEntryState,
    omeMeetOrgUnitEntryInfoState,
    omeMeetTeamEntryState,
    personOrgRoleState,
    onAddEntryClicked,
    onViewTeamEntry,
    onEditTeamEntry,
    onEnterMeetClicked,
    onStartEntryForATeamClicked,
    onDeleteTeamEntryClicked,
    onDeleteTeamEntry,
    onModalCanceled,
    modalState,
    formState, errorState,
    onValueTextPairChanged,
    onFormValueChanged,
    onFilterClicked,
    onClearFilterClicked
  }
}

export default useTeamEntryManagementById;